import { useCallback, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import PageHeader from "../components/PageHeader";
import DataTable, { Column } from "../components/DataTable";
import StatTile from "../components/StatTile";
import Avatar from "../components/Avatar";
import { StatePill, TagChip } from "../components/Pills";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { fetchReferrals } from "../dashboardApi";
import { RefereeRow, ReferralsResponse, ReferrerRow } from "../types";
import { colors, type, viz } from "../styles";
import { formatDate, formatNumber, formatRelative } from "../format";

/**
 * Profile referrals: who referred whom, and whether each referee has met the
 * program's usage requirement.
 *
 * **LIVE, measured rather than assumed.** The whole screen fully expanded is
 * 139 document reads in ~1.3s — 7 referrers, 19 distinct referees, and 113
 * subscription documents behind the message counts. Nothing that small is worth
 * caching.
 *
 * The referrer query looked like it might be expensive and is not: it is a
 * single-field inequality on `profileReferralsLastTimestamp` ordered by that
 * same field, which Firestore's automatic index answers directly. It never
 * scans the `users` collection.
 *
 * Scoping this to current ACTIVE/TRIAL customers was considered and rejected on
 * measurement — that population costs 161 reads against this query's 7, and a
 * creator who churns still referred the people they referred, so scoping would
 * silently drop real referral history.
 *
 * Unlike the legacy screen, referees are resolved up front rather than lazily
 * on row expand. At 19 total the laziness bought nothing and cost the screen its
 * ability to sort or search across referees.
 */

// No subscription (never had one, or it ended) shows as "Free Plan" instead of
// "Inactive", since free-plan signups now count as referrals
const ReferralStatePill = ({ state }: { state: string | null }) =>
  !state || state === "INACTIVE" ? (
    <StatePill state="INACTIVE" label="Free Plan" />
  ) : (
    <StatePill state={state} />
  );

const RefereeDetail = ({
  referee,
  requirements,
}: {
  referee: RefereeRow;
  requirements: { business: number; creator: number };
}) => {
  if (referee.missing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ ...type.bodyMedium, color: colors.inkMuted }}>
          Account no longer exists
        </span>
        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          {referee.uid}
        </span>
      </div>
    );
  }

  const isBusiness = referee.requirement === requirements.business;
  const progress = Math.min(referee.totalMessagesAllTime / referee.requirement, 1);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 220 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...type.bodyMedium, color: colors.ink }}>
            {referee.fullName || `@${referee.username}` || "—"}
          </span>
          <ReferralStatePill state={referee.subscriptionState} />
        </div>
        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          @{referee.username || "—"} · {referee.uid}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 240 }}>
        {/* The requirement is a threshold, so it gets a progress bar rather than
            a bare number — "84,023 messages" means nothing without the 2,500 it
            is being measured against. */}
        <div
          style={{
            flex: 1,
            height: 6,
            borderRadius: 999,
            background: colors.surfaceMuted,
            overflow: "hidden",
            minWidth: 80,
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: "100%",
              background: referee.requirementMet ? viz.good : viz.warning,
            }}
          />
        </div>
        <span
          style={{
            ...type.bodySmallRegular,
            color: colors.inkMuted,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
          title={`${isBusiness ? "Business" : "Creator"} plan requirement`}
        >
          {formatNumber(referee.totalMessagesAllTime)} / {formatNumber(referee.requirement)}
        </span>
        <TagChip
          label={referee.requirementMet ? "Requirements met" : "Not met"}
          icon={referee.requirementMet ? "ion:checkmark-circle" : "ion:alert-circle"}
          tone={referee.requirementMet ? "neutral" : "warning"}
        />
      </div>
    </div>
  );
};

const ReferralsScreen = () => {
  const fetcher = useCallback(() => fetchReferrals(), []);
  const { data, error, loading } = useDashboardData<ReferralsResponse>(fetcher, []);
  const [expanded, setExpanded] = useState<string | undefined>(undefined);

  const referrers = useMemo(() => data?.referrers ?? [], [data]);
  const freshness = { kind: "live" as const };

  const columns: Column<ReferrerRow>[] = [
    {
      key: "referrer",
      label: "Referrer",
      sortValue: row => (row.fullName || row.username).toLowerCase(),
      render: row => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon
            icon={expanded === row.uid ? "ion:chevron-down" : "ion:chevron-forward"}
            height={13}
            style={{ color: colors.inkFaint, flexShrink: 0 }}
          />
          <Avatar
            url={row.profilePicURL}
            fullName={row.fullName}
            username={row.username}
            size={28}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ ...type.bodyMedium, color: colors.ink }}>
              {row.fullName || "—"}
            </span>
            <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
              @{row.username}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "state",
      label: "Status",
      sortValue: row => row.subscriptionState ?? "",
      render: row => <ReferralStatePill state={row.subscriptionState} />,
    },
    {
      key: "count",
      label: "Referrals",
      numeric: true,
      sortValue: row => row.referralCount,
      render: row => formatNumber(row.referralCount),
    },
    {
      key: "fulfilled",
      label: "Requirements met",
      numeric: true,
      sortValue: row => row.fulfilledCount,
      render: row => (
        <span
          style={{
            color: row.fulfilledCount > 0 ? colors.ink : colors.inkFaint,
          }}
          title="Referees who have burned enough text credits to fulfil the referral"
        >
          {formatNumber(row.fulfilledCount)} / {formatNumber(row.referralCount)}
        </span>
      ),
    },
    {
      key: "last",
      label: "Last referral",
      sortValue: row => row.lastReferralAt || "",
      render: row => (
        <span title={formatDate(row.lastReferralAt)}>
          {formatRelative(row.lastReferralAt)}
        </span>
      ),
    },
  ];

  const expandedRow = referrers.find(row => row.uid === expanded);

  return (
    <div>
      <PageHeader
        title="Referrals"
        subtitle="Creators who have referred other creators, and whether those referrals have been fulfilled."
      />

      {error && <ErrorBanner message={error} />}

      {data && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label="Referrers"
              value={formatNumber(data.totals.referrers)}
              secondary="Creators who have referred at least one person"
              freshness={freshness}
            />
            <StatTile
              label="Referrals"
              value={formatNumber(data.totals.referrals)}
              secondary={`${formatNumber(data.totals.distinctReferees)} distinct accounts`}
              freshness={freshness}
            />
            <StatTile
              label="Requirements met"
              value={formatNumber(data.totals.fulfilled)}
              secondary={`${formatNumber(
                data.requirements.creator
              )} credits (creator) / ${formatNumber(
                data.requirements.business
              )} (business)`}
              freshness={freshness}
            />
            <StatTile
              label="Missing accounts"
              value={formatNumber(data.totals.missingReferees)}
              secondary="Referrals pointing at a deleted account"
              tone={data.totals.missingReferees > 0 ? "warning" : "neutral"}
              freshness={freshness}
            />
          </CardGrid>
        </div>
      )}

      {loading && <LoadingNote />}

      {data && (
        <>
          <DataTable
            rows={referrers}
            columns={columns}
            rowKey={row => row.uid}
            onRowClick={row => setExpanded(current => (current === row.uid ? undefined : row.uid))}
            initialSort={{ key: "last", direction: "desc" }}
            emptyLabel="No creator has referred anyone yet."
          />

          {expandedRow && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  background: colors.surface,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    ...type.bodyMedium,
                    color: colors.ink,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    Referred by {expandedRow.fullName || `@${expandedRow.username}`} —{" "}
                    {formatNumber(expandedRow.referralCount)} referral
                    {expandedRow.referralCount === 1 ? "" : "s"}, newest first
                  </span>
                  <button
                    onClick={() => setExpanded(undefined)}
                    style={{
                      ...type.bodySmallMedium,
                      color: colors.inkMuted,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {expandedRow.referees.length === 0 ? (
                    <span style={{ ...type.bodyRegular, color: colors.inkFaint }}>
                      No referees to show.
                    </span>
                  ) : (
                    expandedRow.referees.map(referee => (
                      <div
                        key={referee.uid}
                        style={{
                          borderTop: `1px solid ${colors.border}`,
                          paddingTop: 12,
                        }}
                      >
                        <RefereeDetail
                          referee={referee}
                          requirements={data.requirements}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <NoteCard title="A referral is fulfilled by usage, not by signing up">
              A referee counts as fulfilled once they have sent{" "}
              {formatNumber(data.requirements.creator)} text credits on a creator plan,
              or {formatNumber(data.requirements.business)} on a Business plan — the
              thresholds published in Markit&rsquo;s referral program docs. The count
              is every message across their past and current subscriptions. Referrals
              pointing at an account that has since been deleted are listed rather
              than dropped, so a referrer&rsquo;s referral count always reconciles
              with the referees shown under them.
            </NoteCard>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferralsScreen;
